<!-- src/components/trends/TrendsOverview.vue -->
<!-- 회원권 추세 "추세" 탭: 타일 → 월별 표 → 매출 나눔 표 → 차트 셋 -->
<!-- (docs/architecture/admin-trends-design.md 1.4·2.1·4.3절). 불러오기·오류·빈 상태는 TrendsView가 그린다 -->
<template>
  <div class="overview">
    <!-- 타일: 큰 값 + 짧은 정의(늘 보임) -->
    <div class="tiles">
      <div class="tile">
        <span class="tile-k">활성 회원 <small>오늘</small></span>
        <span class="tile-v">
          <!-- 값이 없으면(기록 전 달) 단위 없이 "—"만 -->
          <span
            >{{ count(current && current.active)
            }}<small v-if="isNum(current && current.active)">명</small></span
          >
          <span
            v-if="activeDelta !== null"
            class="tile-delta"
            :class="deltaTone(activeDelta)"
            >{{ deltaText(activeDelta) }}</span
          >
        </span>
        <span v-if="activeDelta !== null" class="tile-sub"
          >지난달 말({{ prevMonthText }}) 대비</span
        >
        <span class="tile-def">{{ short.active }}</span>
      </div>

      <div class="tile">
        <span class="tile-k"
          >신규 <small v-if="prev">({{ prevMonthText }})</small></span
        >
        <span class="tile-v"
          ><span
            >{{ count(prevMember("new"))
            }}<small v-if="isNum(prevMember('new'))">명</small></span
          ></span
        >
        <span v-if="cur" class="tile-sub">
          이번 달 {{ shortDate(cur.as_of) }}까지 {{ count(cur.new) }}명 · 지난달
          같은 기간 {{ count(cur.new_prev_same_period) }}명
        </span>
        <span class="tile-def">{{ short.new }}</span>
      </div>

      <div class="tile">
        <span class="tile-k"
          >이탈률
          <small v-if="finalRow">({{ finalMonthText }} 확정)</small></span
        >
        <span class="tile-v" :class="{ muted: finalSmall }">{{
          finalRow ? pct(finalRow.decisions.churn_rate) : "—"
        }}</span>
        <span v-if="finalRow" class="tile-sub">
          {{ count(finalDen) }}건 중 {{ count(finalRow.decisions.churned) }}건
          이탈<template v-if="finalSmall"> · {{ SMALL_SAMPLE_LABEL }}</template>
        </span>
        <span v-else class="tile-sub">판정이 끝난 달이 아직 없습니다</span>
        <span class="tile-def">{{ short.churn_rate }}</span>
      </div>

      <div class="tile">
        <span class="tile-k"
          >재등록률
          <small v-if="finalRow">({{ finalMonthText }} 확정)</small></span
        >
        <span class="tile-v" :class="{ muted: finalSmall }">{{
          finalRow ? pct(finalRow.decisions.renewal_rate) : "—"
        }}</span>
        <span v-if="finalRow" class="tile-sub">
          {{ count(finalDen) }}건 중 {{ count(finalRow.decisions.renewed) }}건
          재등록<template v-if="finalSmall">
            · {{ SMALL_SAMPLE_LABEL }}</template
          >
        </span>
        <span v-else class="tile-sub">판정이 끝난 달이 아직 없습니다</span>
        <span class="tile-def">{{ short.renewal_rate }}</span>
      </div>

      <div class="tile tile--wide">
        <span class="tile-k"
          >매출 <small v-if="prev">({{ prevMonthText }})</small></span
        >
        <span class="tile-v">{{
          manwon(prev && prev.revenue ? prev.revenue.total : null)
        }}</span>
        <span v-if="cur" class="tile-sub">
          이번 달 {{ shortDate(cur.as_of) }}까지 {{ manwon(cur.revenue) }} ·
          지난달 같은 기간 {{ manwon(cur.revenue_prev_same_period) }}
        </span>
        <span v-if="cur && cur.upcoming" class="tile-sub">
          다음 달 이후 시작 {{ count(cur.upcoming.count) }}건 ·
          {{ manwon(cur.upcoming.amount) }}
        </span>
        <span class="tile-def">{{ short.revenue }}</span>
      </div>
    </div>

    <!-- 월별 표 -->
    <section class="block" aria-labelledby="ov-monthly-title">
      <div class="block-head">
        <h3 id="ov-monthly-title">월별</h3>
        <div class="seg" role="group" aria-label="수강권 종류">
          <span class="seg-k">종류</span>
          <button
            v-for="t in typeOptions"
            :key="t.value"
            type="button"
            :class="['seg-btn', { active: type === t.value }]"
            :aria-pressed="type === t.value"
            @click="type = t.value"
          >
            {{ t.label }}
          </button>
        </div>
      </div>
      <p class="block-def">{{ definitions.overview_table }}</p>
      <p v-if="type !== 'all'" class="block-def">
        {{ type }} 값으로 바꿨습니다. 신규·복귀·증감은 회원 단위라 "—"로 둡니다.
        위 타일과 아래 매출 나눔 표·차트는 전체 기준 그대로입니다.
      </p>

      <!-- 좁은 화면에서 옆으로 넘치는 표: 키보드로도 초점을 받아 ←/→로 밀 수 있게(체험 전환 표와 같음, 리뷰 5차) -->
      <div class="table-wrap" role="region" aria-label="월별 표" tabindex="0">
        <table class="t-table">
          <caption class="sr-only">
            최근
            {{
              months
            }}개월 월별 활성 회원·신규·복귀·재등록·이탈·매출 ({{
              type === "all" ? "전체" : type
            }})
          </caption>
          <thead>
            <tr>
              <th scope="col" class="col-month">월</th>
              <th scope="col">활성</th>
              <th scope="col">증감</th>
              <th scope="col">신규</th>
              <th scope="col">복귀</th>
              <th scope="col">재등록</th>
              <th scope="col">이탈</th>
              <th scope="col">판정 대기</th>
              <th scope="col">미리 재등록</th>
              <th scope="col">재등록률</th>
              <th scope="col">이탈률</th>
              <th scope="col">매출</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="r in tableRows"
              :key="r.month"
              :class="{ 'row-partial': r.inProgress, 'row-nodata': r.noData }"
            >
              <th scope="row" class="col-month">
                <span class="month">{{ monthLabel(r.month) }}</span>
                <span v-if="r.inProgress" class="badge tone-muted">{{
                  IN_PROGRESS_LABEL
                }}</span>
                <span v-if="r.noData" class="badge tone-muted">{{
                  NO_DATA_LABEL
                }}</span>
              </th>
              <td>{{ count(r.active) }}</td>
              <td :class="deltaTone(r.delta)">
                {{ r.delta === null ? "—" : deltaText(r.delta) }}
              </td>
              <td>{{ count(r.newCount) }}</td>
              <td>{{ count(r.returned) }}</td>
              <td>{{ count(r.renewed) }}</td>
              <td>{{ count(r.churned) }}</td>
              <td>{{ count(r.pending) }}</td>
              <td>{{ count(r.early) }}</td>
              <td :class="{ muted: r.renewalCell.muted }">
                <span class="rate">{{ r.renewalCell.main }}</span>
                <span v-if="r.renewalCell.sub" class="sub">{{
                  r.renewalCell.sub
                }}</span>
              </td>
              <td :class="{ muted: r.churnCell.muted }">
                <span class="rate">{{ r.churnCell.main }}</span>
                <span v-if="r.churnCell.sub" class="sub">{{
                  r.churnCell.sub
                }}</span>
              </td>
              <td class="money">{{ won(r.revenue) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- 매출 나눔 표: 등록 구분(신규·재등록·복귀)과 결제 수단은 각각 합계를 나눈 것이라 두 묶음 사이에 세로선.
         늘 전체 기준이라 월별 표의 종류를 고른 동안 제목에 "전체 기준"을 붙인다(차트와 같음) -->
    <section class="block" aria-labelledby="ov-revenue-title">
      <div class="block-head">
        <h3 id="ov-revenue-title">
          매출 나눔<small v-if="chartScope" class="chart-scope">{{
            chartScope
          }}</small>
        </h3>
      </div>
      <p class="block-def">
        {{ definitions.revenue }} 두 묶음은 각각 합계를 나눈 것이라 더하면
        합계의 두 배입니다.
      </p>
      <div
        class="table-wrap"
        role="region"
        aria-label="매출 나눔 표"
        tabindex="0"
      >
        <table class="t-table t-revenue">
          <caption class="sr-only">
            월별 매출 나눔 (등록 구분별·결제 수단별, 전체 기준)
          </caption>
          <thead>
            <tr class="group-row">
              <th scope="col" class="col-month" rowspan="2">월</th>
              <th scope="colgroup" colspan="3" class="group-sep">
                등록 구분별
              </th>
              <th scope="colgroup" colspan="3" class="group-sep">
                결제 수단별
              </th>
              <th scope="col" rowspan="2" class="group-sep">합계</th>
            </tr>
            <tr>
              <th
                v-for="(k, i) in revenueKinds"
                :key="k.value"
                scope="col"
                :class="{ 'group-sep': i === 0 }"
              >
                {{ k.label }}
              </th>
              <th
                v-for="(m, i) in paymentMethods"
                :key="m.value"
                scope="col"
                :class="{ 'group-sep': i === 0 }"
              >
                {{ m.label }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="r in revenueRows"
              :key="r.month"
              :class="{ 'row-partial': r.inProgress, 'row-nodata': r.noData }"
            >
              <th scope="row" class="col-month">
                <span class="month">{{ monthLabel(r.month) }}</span>
                <span v-if="r.inProgress" class="badge tone-muted">{{
                  IN_PROGRESS_LABEL
                }}</span>
                <span v-if="r.noData" class="badge tone-muted">{{
                  NO_DATA_LABEL
                }}</span>
              </th>
              <td
                v-for="(c, i) in r.kinds"
                :key="c.key"
                class="money"
                :class="{ 'group-sep': i === 0 }"
              >
                <span>{{ won(c.amount) }}</span>
                <span v-if="isNum(c.count)" class="sub"
                  >{{ count(c.count) }}건</span
                >
              </td>
              <td
                v-for="(c, i) in r.payments"
                :key="c.key"
                class="money"
                :class="{ 'group-sep': i === 0 }"
              >
                <span>{{ won(c.amount) }}</span>
                <span v-if="isNum(c.count)" class="sub"
                  >{{ count(c.count) }}건</span
                >
              </td>
              <td class="money group-sep strong">
                <span>{{ won(r.total) }}</span>
                <span v-if="isNum(r.count)" class="sub"
                  >{{ count(r.count) }}건</span
                >
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- 차트(늘 전체 기준): 숫자는 위 표에 있다. 표의 종류를 고르면 제목에 "전체 기준"을 붙여 그 종류 값으로 읽지 않게 한다 -->
    <section class="charts" aria-label="추세 차트">
      <div class="chart-card">
        <h3 class="chart-title">
          활성 회원<small v-if="chartScope" class="chart-scope">{{
            chartScope
          }}</small>
        </h3>
        <TrendChart
          kind="line"
          :labels="labels"
          :series="activeSeries"
          unit="count"
          :partial-last="hasPartialLast"
          :aria-label="`활성 회원${chartScope}`"
          :height="chartHeight"
        />
      </div>
      <div class="chart-card">
        <h3 class="chart-title">
          이탈률<small v-if="chartScope" class="chart-scope">{{
            chartScope
          }}</small>
        </h3>
        <TrendChart
          kind="line"
          :labels="labels"
          :series="churnSeries"
          unit="pct"
          :partial-last="hasPartialLast"
          :aria-label="`이탈률${chartScope}`"
          :height="chartHeight"
        />
      </div>
      <div class="chart-card">
        <h3 class="chart-title">
          매출<small v-if="chartScope" class="chart-scope">{{
            chartScope
          }}</small>
        </h3>
        <TrendChart
          kind="stacked-bar"
          :labels="labels"
          :series="revenueSeries"
          unit="manwon"
          :partial-last="hasPartialLast"
          :aria-label="`매출(신규·재등록·복귀)${chartScope}`"
          :height="chartHeight"
        />
      </div>
    </section>
  </div>
</template>

<script>
import TrendChart from "@/components/trends/TrendChart.vue";
import {
  IN_PROGRESS_LABEL,
  MEMBERSHIP_TYPES,
  METRIC_DEFINITIONS,
  METRIC_SHORT,
  NO_DATA_LABEL,
  OPEN_LABEL,
  PAYMENT_METHODS,
  REVENUE_KINDS,
  SMALL_SAMPLE_LABEL,
  count,
  deltaText,
  isSmallSample,
  manwon,
  monthLabel,
  monthLabelShort,
  pct,
  pick,
  won,
} from "@/lib/trends";
import { shortDate } from "@/lib/memberNotes";

const WIDE_CHARTS_QUERY = "(min-width: 1280px)";

function isNum(x) {
  return typeof x === "number" && Number.isFinite(x);
}

// 비율 칸: 위는 비율, 아래 작은 글은 "(41/52)"와 집계 중·표본 적음. 분모 0이면 "—"
function rateCell(rate, num, den, final) {
  if (!isNum(den) || den <= 0 || !isNum(rate)) {
    return { main: "—", sub: final ? "" : OPEN_LABEL, muted: !final };
  }
  const notes = [`(${count(num)}/${count(den)})`];
  if (!final) notes.push(OPEN_LABEL);
  else if (isSmallSample(den)) notes.push(SMALL_SAMPLE_LABEL);
  return {
    main: pct(rate),
    sub: notes.join(" · "),
    muted: !final || isSmallSample(den),
  };
}

export default {
  name: "TrendsOverview",
  components: { TrendChart },
  props: {
    // GET /v1/admin/trends/overview 응답(2.1절)
    data: { type: Object, required: true },
    months: { type: Number, default: 12 },
  },
  data() {
    return {
      IN_PROGRESS_LABEL,
      NO_DATA_LABEL,
      SMALL_SAMPLE_LABEL,
      short: METRIC_SHORT,
      definitions: METRIC_DEFINITIONS,
      revenueKinds: REVENUE_KINDS,
      paymentMethods: PAYMENT_METHODS,
      // 월별 표의 수강권 종류(화면 안 상태라 주소에 담지 않는다)
      type: "all",
      wideCharts: false,
    };
  },
  computed: {
    typeOptions() {
      return [{ value: "all", label: "전체" }, ...MEMBERSHIP_TYPES];
    },
    // 매출 나눔 표와 차트는 늘 전체 값이다. 월별 표의 종류를 고른 동안 그 제목·aria-label에 " (전체 기준)"
    chartScope() {
      return this.type === "all" ? "" : " (전체 기준)";
    },
    rows() {
      return Array.isArray(this.data && this.data.rows) ? this.data.rows : [];
    },
    labels() {
      return this.rows.map((r) => r.month);
    },
    // 이번 달 줄(진행 중)과 끝난 지난달 줄
    current() {
      return this.rows.length ? this.rows[this.rows.length - 1] : null;
    },
    prev() {
      return this.rows.length > 1 ? this.rows[this.rows.length - 2] : null;
    },
    cur() {
      return (this.data && this.data.current) || null;
    },
    prevMonthText() {
      return this.prev ? monthLabelShort(this.prev.month) : "";
    },
    // 활성 회원 타일: 오늘 − 지난달 말
    activeDelta() {
      const a = this.current && this.current.active;
      const b = this.prev && !this.prev.no_data ? this.prev.active : null;
      return isNum(a) && isNum(b) ? a - b : null;
    },
    // 이탈률·재등록률 타일: 판정이 다 끝난 가장 최근 달
    finalRow() {
      const m = this.data && this.data.latest_final_month;
      if (!m) return null;
      const row = this.rows.find((r) => r.month === m);
      return row && row.decisions ? row : null;
    },
    finalMonthText() {
      return this.finalRow ? monthLabelShort(this.finalRow.month) : "";
    },
    finalDen() {
      const d = this.finalRow && this.finalRow.decisions;
      if (!d) return null;
      return (d.renewed || 0) + (d.churned || 0);
    },
    finalSmall() {
      return isSmallSample(this.finalDen);
    },
    hasPartialLast() {
      return !!(this.current && this.current.in_progress);
    },
    // 월별 표: 최신 달이 위. 종류를 고르면 활성·판정·비율·매출을 그 종류 값으로
    tableRows() {
      const t = this.type;
      const out = this.rows.map((r, i) => {
        const older = i > 0 ? this.rows[i - 1] : null;
        const noData = !!r.no_data;
        const dec =
          t === "all"
            ? r.decisions || null
            : pick(r.decisions_by_type, "type", t);
        const final = !!(r.decisions && r.decisions.final);
        const active =
          t === "all"
            ? r.active
            : (pick(r.active_by_type, "type", t) || {}).count;
        const revenue =
          t === "all"
            ? r.revenue && r.revenue.total
            : (pick(r.revenue && r.revenue.by_type, "type", t) || {}).amount;
        let delta = null;
        if (
          t === "all" &&
          !noData &&
          older &&
          !older.no_data &&
          isNum(r.active) &&
          isNum(older.active)
        ) {
          delta = r.active - older.active;
        }
        const renewed = dec ? dec.renewed : null;
        const churned = dec ? dec.churned : null;
        const den = isNum(renewed) && isNum(churned) ? renewed + churned : null;
        return {
          month: r.month,
          inProgress: !!r.in_progress,
          noData,
          active: noData ? null : active,
          delta,
          newCount: t === "all" && !noData ? r.new : null,
          returned: t === "all" && !noData ? r.returned : null,
          renewed: noData ? null : renewed,
          churned: noData ? null : churned,
          pending: noData || !dec ? null : dec.pending,
          early: noData || !dec ? null : dec.early_renewed,
          renewalCell: noData
            ? { main: "—", sub: "", muted: true }
            : rateCell(dec && dec.renewal_rate, renewed, den, final),
          churnCell: noData
            ? { main: "—", sub: "", muted: true }
            : rateCell(dec && dec.churn_rate, churned, den, final),
          revenue,
        };
      });
      return out.reverse();
    },
    revenueRows() {
      return this.rows
        .map((r) => {
          const rev = r.revenue || {};
          return {
            month: r.month,
            inProgress: !!r.in_progress,
            noData: !!r.no_data,
            kinds: REVENUE_KINDS.map((k) => {
              const c = pick(rev.by_kind, "kind", k.value) || {};
              return { key: k.value, amount: c.amount, count: c.count };
            }),
            payments: PAYMENT_METHODS.map((m) => {
              const c = pick(rev.by_payment, "method", m.value) || {};
              return { key: m.value, amount: c.amount, count: c.count };
            }),
            total: rev.total,
            count: rev.count,
          };
        })
        .reverse();
    },
    activeSeries() {
      return [
        {
          key: "active",
          label: "활성 회원",
          values: this.rows.map((r) => (r.no_data ? null : r.active)),
        },
      ];
    },
    // 이탈률: 분모 0·기록 전은 끊고, 판정이 덜 끝난 달·표본 적은 달은 속 빈 원. 속 빈 원은 그리는 점에만 둔다
    // (리뷰 3차: 기록 전 달은 decisions가 남아 분모 0이 "표본 적음"이 되어, 그리지 않는 점 때문에 "속 빈 점은 …" 설명이 붙었다)
    churnValues() {
      return this.rows.map((r) => {
        const d = r.decisions;
        if (r.no_data || !d) return null;
        const den = (d.renewed || 0) + (d.churned || 0);
        return den > 0 && isNum(d.churn_rate) ? d.churn_rate : null;
      });
    },
    churnSeries() {
      const values = this.churnValues;
      return [
        {
          key: "churn",
          label: "이탈률",
          values,
          hollow: this.rows.map((r, i) => {
            const d = r.decisions;
            if (!d || !isNum(values[i])) return false;
            const den = (d.renewed || 0) + (d.churned || 0);
            return !d.final || isSmallSample(den);
          }),
          // 툴팁에 붙이는 까닭(표의 비율 칸과 같은 말): 판정이 안 끝났으면 "집계 중", 아니면 "표본 적음 (n/N)"
          notes: this.rows.map((r) => {
            const d = r.decisions;
            if (!d) return "";
            const den = (d.renewed || 0) + (d.churned || 0);
            if (!d.final) return OPEN_LABEL;
            return isSmallSample(den)
              ? `${SMALL_SAMPLE_LABEL} (${count(d.churned)}/${count(den)})`
              : "";
          }),
        },
      ];
    },
    // 매출 쌓은 막대: 신규·재등록·복귀(늘 이 순서, 색 1·2·3번)
    revenueSeries() {
      return REVENUE_KINDS.map((k) => ({
        key: k.value,
        label: k.label,
        values: this.rows.map((r) => {
          const c = pick(r.revenue && r.revenue.by_kind, "kind", k.value);
          return c && isNum(c.amount) ? c.amount : null;
        }),
      }));
    },
    // 1280px 이상에서는 세 차트를 한 줄에 높이 160으로(4.3절)
    chartHeight() {
      return this.wideCharts ? 160 : 200;
    },
  },
  mounted() {
    if (typeof window.matchMedia === "function") {
      this.mql = window.matchMedia(WIDE_CHARTS_QUERY);
      this.wideCharts = this.mql.matches;
      if (this.mql.addEventListener) {
        this.mql.addEventListener("change", this.onWideChange);
      } else if (this.mql.addListener) {
        this.mql.addListener(this.onWideChange);
      }
    }
  },
  beforeUnmount() {
    if (!this.mql) return;
    if (this.mql.removeEventListener) {
      this.mql.removeEventListener("change", this.onWideChange);
    } else if (this.mql.removeListener) {
      this.mql.removeListener(this.onWideChange);
    }
  },
  methods: {
    isNum,
    count,
    pct,
    won,
    manwon,
    deltaText,
    monthLabel,
    shortDate,
    onWideChange(event) {
      this.wideCharts = !!event.matches;
    },
    // 기록 전 달의 회원 지표는 "—"
    prevMember(key) {
      if (!this.prev || this.prev.no_data) return null;
      return this.prev[key];
    },
    deltaTone(n) {
      if (!isNum(n) || n === 0) return "";
      return n > 0 ? "up" : "down";
    },
  },
};
</script>

<style scoped>
.overview {
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
}

/* ---------------- 타일 ---------------- */
.tiles {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
}

.tile {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  padding: 12px 14px;
  border-radius: 10px;
  background: #0a0a0a;
  border: 1px solid #1f1f1f;
}

.tile-k {
  font-size: 13px;
  color: #8e8e8e;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

/* "(9월)"·"(9월 확정)": 카드 #151718 위 5.6:1(리뷰 3차: #6b7280은 3.7:1) */
.tile-k small {
  font-size: 12px;
  color: #8e8e8e;
}

.tile-v {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 2px 8px;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
  color: #fff;
}

.tile-v small {
  margin-left: 2px;
  font-size: 14px;
  font-weight: 400;
  color: #8e8e8e;
}

.tile-v.muted {
  color: #9ca3af;
}

.tile-delta {
  font-size: 14px;
  font-weight: 600;
  color: #9ca3af;
}

.tile-delta.up {
  color: #22c55e;
}

.tile-delta.down {
  color: #ef4444;
}

.tile-sub {
  font-size: 12px;
  line-height: 1.45;
  color: #c8c8c8;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.tile-def {
  margin-top: auto;
  padding-top: 4px;
  font-size: 12px;
  line-height: 1.4;
  color: #8e8e8e;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

/* ---------------- 표 묶음 ---------------- */
.block {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.block-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 12px;
}

.block h3,
.chart-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
}

.block-def {
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: #9d9d9d;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

/* 종류 고르기 (붙은 버튼 한 묶음) */
.seg {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.seg-k {
  font-size: 12px;
  color: #8e8e8e;
}

.seg-btn {
  height: 30px;
  padding: 0 10px;
  border-radius: 6px;
  border: 1px solid #333;
  background: #1b1d1e;
  color: #9d9d9d;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}

.seg-btn:hover:not(.active) {
  background: #252728;
  color: #fff;
}

.seg-btn.active {
  background: #78ff47;
  border-color: #78ff47;
  color: #000;
  font-weight: 600;
}

.seg-btn:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 1px;
}

/* 넓은 표는 자기 상자 안에서만 가로로 넘기고 첫 칸(월)은 붙여 둔다 */
.table-wrap {
  width: 100%;
  min-width: 0;
  overflow-x: auto;
  border: 1px solid #1f1f1f;
  border-radius: 8px;
  background: #0a0a0a;
}

.table-wrap:focus-visible {
  outline: 2px solid #78ff47;
  outline-offset: 2px;
}

.t-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}

.t-table th,
.t-table td {
  padding: 8px 10px;
  border-bottom: 1px solid #1f1f1f;
  text-align: right;
  white-space: nowrap;
  vertical-align: top;
}

.t-table thead th {
  position: sticky;
  top: 0;
  font-size: 12px;
  font-weight: 400;
  color: #8e8e8e;
  background: #0a0a0a;
  vertical-align: bottom;
}

.t-table tbody tr:last-child th,
.t-table tbody tr:last-child td {
  border-bottom: 0;
}

.t-table tbody tr:hover td,
.t-table tbody tr:hover th {
  background: #121314;
}

.t-table .col-month {
  position: sticky;
  left: 0;
  z-index: 1;
  text-align: left;
  background: #0a0a0a;
  color: #fff;
  font-weight: 600;
  box-shadow: 1px 0 0 #1f1f1f;
}

.t-table thead .col-month {
  z-index: 2;
  color: #8e8e8e;
  font-weight: 400;
}

.month {
  margin-right: 6px;
}

.t-table td .rate,
.t-table td > span:first-child {
  display: block;
}

.t-table .sub {
  display: block;
  margin-top: 2px;
  font-size: 11px;
  color: #8e8e8e;
}

.t-table td.muted .rate {
  color: #9ca3af;
}

.t-table td.up {
  color: #22c55e;
}

.t-table td.down {
  color: #ef4444;
}

.t-table .strong {
  font-weight: 700;
}

/* 기록 전 달의 "—"는 #0a0a0a 위 6:1(리뷰 6차: #6b7280은 4.1:1. 체험 전환·운영의 기록 전 칸과 같은 색) */
.row-nodata td {
  color: #8e8e8e;
}

/* 등록 구분·결제 수단 두 묶음 사이 세로선 */
.t-revenue .group-sep {
  border-left: 1px solid #333;
}

.t-revenue .group-row th {
  text-align: center;
  color: #c8c8c8;
}

/* 배지 (다른 화면과 같은 tone 색) */
.badge {
  display: inline-flex;
  align-items: center;
  height: 18px;
  padding: 0 6px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  vertical-align: middle;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
  color: #c8c8c8;
}

/* 이번 달이 기록 전이면 "진행 중"·"기록 전" 두 배지가 붙지 않게(빌드가 둘 사이 빈칸을 지운다, 리뷰 6차) */
.badge + .badge {
  margin-left: 4px;
}

.badge.tone-muted {
  color: #9ca3af;
  background: rgba(156, 163, 175, 0.1);
  border-color: rgba(156, 163, 175, 0.3);
}

/* ---------------- 차트 ---------------- */
.charts {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 12px;
}

.chart-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  padding: 12px 14px;
  border-radius: 10px;
  background: #151718;
  border: 1px solid #1f1f1f;
}

.chart-title {
  font-size: 14px;
}

/* 표의 종류를 고른 동안 매출 나눔·차트 제목 옆 "(전체 기준)" */
.chart-scope {
  margin-left: 4px;
  font-size: 12px;
  font-weight: 400;
  color: #9ca3af;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* ---------------- 반응형 ---------------- */
@media (min-width: 1280px) {
  .charts {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 1280px) {
  .tiles {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .tile-v {
    font-size: 20px;
  }
}

@media (max-width: 720px) {
  /* 타일 2열, 마지막(매출) 하나는 한 줄 */
  .tiles {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  .tile {
    padding: 10px 12px;
  }

  .tile--wide {
    grid-column: 1 / -1;
  }

  .t-table th,
  .t-table td {
    padding: 7px 8px;
  }
}
</style>
